// Besucherzahlen schwalbennest-ruegen.com (cookielos): Verlauf 30 Tage, beliebteste Seiten, Herkunft, Buchen-Klicks
import React, { useState, useEffect } from "react";
import axios from "axios";
import { BarChart3, MousePointerClick } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/com/stats`;
const day = (iso) => { const d = new Date(`${iso}T12:00:00`); return `${d.getDate()}.${d.getMonth() + 1}.`; };

function Kpi({ label, value, testid }) {
  return (
    <div className="bg-black/25 rounded-lg p-3" data-testid={testid}>
      <div className="text-lg font-semibold">{value}</div>
      <div className="text-[11px] text-eb-muted uppercase tracking-wider mt-0.5">{label}</div>
    </div>
  );
}

function Bars({ days }) {
  const max = Math.max(1, ...days.map((d) => d.visitors));
  return (
    <div>
      <div className="flex items-end gap-[3px] h-28" data-testid="com-stats-chart">
        {days.map((d) => (
          <div key={d.date} className="flex-1 h-full flex flex-col justify-end group"
            title={`${day(d.date)}: ${d.visitors} Besucher · ${d.pageviews} Aufrufe · ${d.book} Buchen-Klicks`}>
            <div className="rounded-t bg-sky-400/70 group-hover:bg-sky-300 transition-colors" style={{ height: `${(100 * d.visitors) / max}%`, minHeight: d.visitors ? 3 : 1 }} />
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-eb-muted mt-1">
        <span>{day(days[0].date)}</span><span>{day(days[Math.floor(days.length / 2)].date)}</span><span>heute</span>
      </div>
    </div>
  );
}

function TopList({ title, items, testid, empty }) {
  const max = Math.max(1, ...items.map((i) => i.n));
  return (
    <div data-testid={testid}>
      <div className="text-[11px] text-eb-muted uppercase tracking-wider mb-2">{title}</div>
      {items.length ? items.map((i) => (
        <div key={i.key} className="relative text-xs py-1 px-2 rounded mb-1 overflow-hidden">
          <div className="absolute inset-y-0 left-0 bg-white/5" style={{ width: `${(100 * i.n) / max}%` }} />
          <div className="relative flex justify-between gap-2"><span className="truncate">{i.title}</span><b>{i.n}</b></div>
        </div>
      )) : <p className="text-xs text-eb-muted">{empty}</p>}
    </div>
  );
}

export function VisitorStats() {
  const [d, setD] = useState(null);
  const [msg, setMsg] = useState("");
  useEffect(() => { axios.get(API).then((r) => setD(r.data)).catch((e) => setMsg(e.response?.data?.detail || "Besucherzahlen nicht ladbar")); }, []);
  if (!d) return msg ? <p className="text-sm text-rose-400" data-testid="com-stats-error">{msg}</p> : null;
  const t = d.totals;
  return (
    <div className="bg-eb-surface border border-white/5 rounded-xl p-5 space-y-4" data-testid="com-stats">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-heading font-semibold flex items-center gap-2"><BarChart3 size={18} className="text-sky-300" /> Besucher auf schwalbennest-ruegen.com</h2>
        <span className="text-[11px] text-eb-muted">ohne Cookies · kein Banner nötig · letzte 30 Tage</span>
      </div>
      {!t.pageviews && <p className="text-xs text-amber-300" data-testid="com-stats-empty">Noch keine Besuche gezählt – die Zählung startet automatisch, sobald das Schwalbennest-Theme auf der .com aktiv ist.</p>}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Kpi label="Besucher heute" value={d.today.visitors} testid="com-stats-today" />
        <Kpi label="Besucher gestern" value={d.yesterday ? d.yesterday.visitors : "–"} testid="com-stats-yesterday" />
        <Kpi label="Besucher 30 Tage" value={t.visitors} testid="com-stats-visitors" />
        <Kpi label="Seitenaufrufe 30 Tage" value={t.pageviews} testid="com-stats-pageviews" />
        <Kpi label={`Buchen-Klicks${d.book_rate != null ? ` · ${d.book_rate.toLocaleString("de-DE")} %` : ""}`} value={t.book} testid="com-stats-book" />
      </div>
      <Bars days={d.days} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <TopList title="Beliebteste Seiten" items={d.top_pages} testid="com-stats-pages" empty="Noch keine Aufrufe" />
        <TopList title="Woher die Besucher kommen" items={d.sources.map((s) => ({ ...s, title: s.key }))} testid="com-stats-sources" empty="Noch keine Besucher" />
        <TopList title={<span className="flex items-center gap-1"><MousePointerClick size={11} /> „Buchen“ geklickt auf</span>} items={d.book_pages} testid="com-stats-book-pages" empty="Noch keine Buchen-Klicks" />
      </div>
      <p className="text-[11px] text-eb-muted">Gezählt wird ein Besucher pro Tag über einen täglich wechselnden, nicht umkehrbaren Prüfwert – keine IP-Speicherung, nichts im Browser des Gastes. Suchmaschinen-Bots werden herausgefiltert.</p>
    </div>
  );
}
